import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { toast } from 'sonner';

export interface SizeSpec {
  id: string;
  platform: string;
  format: string;
  aspect_ratio?: string;
  recommended_px?: string;
  min_px?: string;
  max_file_size?: string;
  notes?: string;
  last_verified?: string;
  is_global: boolean;
  brand_id?: string;
  created_at: string;
  updated_at: string;
}

export const useSizes = () => {
  const [sizes, setSizes] = useState<SizeSpec[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentBrandId } = useBrandManager();

  const fetchSizes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sm_visual_sizes')
        .select('*')
        .order('platform', { ascending: true })
        .order('format', { ascending: true });

      if (error) throw error;
      setSizes(data || []);
    } catch (error) {
      console.error('Error fetching sizes:', error);
      toast.error('Failed to load size specifications');
    } finally {
      setLoading(false);
    }
  };

  const addSize = async (newSize: Omit<SizeSpec, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('sm_visual_sizes')
        .insert([{
          ...newSize,
          brand_id: currentBrandId,
          is_global: false
        }])
        .select()
        .single();

      if (error) throw error;
      setSizes(prev => [...prev, data]);
      toast.success('Size specification added');
    } catch (error) {
      console.error('Error adding size:', error);
      toast.error('Failed to add size specification');
    }
  };

  const updateSize = async (id: string, updates: Partial<SizeSpec>) => {
    try {
      const { data, error } = await supabase
        .from('sm_visual_sizes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setSizes(prev => prev.map(size => size.id === id ? data : size));
      toast.success('Size specification updated');
    } catch (error) {
      console.error('Error updating size:', error);
      toast.error('Failed to update size specification');
    }
  };

  const deleteSize = async (id: string) => {
    try {
      const { error } = await supabase
        .from('sm_visual_sizes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSizes(prev => prev.filter(size => size.id !== id));
      toast.success('Size specification deleted');
    } catch (error) {
      console.error('Error deleting size:', error);
      toast.error('Failed to delete size specification');
    }
  };

  useEffect(() => {
    fetchSizes();
  }, [currentBrandId]);

  return {
    sizes,
    loading,
    addSize,
    updateSize,
    deleteSize,
    refreshSizes: fetchSizes
  };
};